import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { Trash2, WifiOff } from "lucide-react";
import EmptyState from "../../CommonComponents/EmptyState";
import PrimaryButton from "../../CommonComponents/PrimaryButton";
import SearchInput from "../../CommonComponents/SearchInput";
import Pagination from "../../CommonComponents/Pagination";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from "../../../utils/tableStyle";
import { authStorage } from "../../../utils/authStorage";
import { getAllPOSProducts } from "../../../services/posProductServices";
import { createDisposal, fetchDisposals } from "../../../services/disposalServices";

const DisposalPage = () => {
  const [tab, setTab] = useState("dispose");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const rowsPerPage = 10;

  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const user = authStorage.getUser();
      const branchId = user?.branch_id;
      const response = await getAllPOSProducts(branchId);
      const bakery = (Array.isArray(response) ? response : []).filter((p) =>
        String(p?.category || "").trim().toLowerCase() === "bakery product"
      );
      setProducts(bakery);
    } catch (err) {
      setProductsError(err);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchDisposals({
        page: currentPage,
        limit: rowsPerPage,
        search: searchTerm,
        startDate: appliedStartDate,
        endDate: appliedEndDate,
      });
      setRows(Array.isArray(response?.data) ? response.data : []);
      setTotalPages(Math.max(Number(response?.pagination?.totalPages) || 1, 1));
      setTotalCount(Number(response?.pagination?.total) || 0);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchTerm, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const productOptions = useMemo(
    () => products.map((product) => ({ value: String(product.id), label: product.name })),
    [products]
  );

  const handleSubmitDisposal = async (e) => {
    e.preventDefault();
    if (!productId || !qty || Number(qty) <= 0) {
      toast.error("Select a bakery product and quantity.");
      return;
    }
    setSaving(true);
    try {
      await createDisposal({
        product_id: Number(productId),
        qty: Number(qty),
        reason: reason.trim(),
      });
      toast.success("Disposal recorded.");
      setProductId("");
      setQty(1);
      setReason("");
      loadProducts();
      loadLogs();
      setTab("logs");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to record disposal.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  const formatTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="pt-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ color: "var(--color-text)" }}>Disposal</h2>
        <p className="text-sm text-gray-500">Record bakery waste and review waste logs.</p>
      </div>

      <div className="flex gap-2 mb-6">
        <button
          type="button"
          className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "dispose" ? "text-white" : "text-gray-700 bg-gray-100 hover:bg-gray-200"}`}
          style={tab === "dispose" ? { background: "var(--color-primary)" } : {}}
          onClick={() => setTab("dispose")}
        >
          Disposal
        </button>
        <button
          type="button"
          className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "logs" ? "text-white" : "text-gray-700 bg-gray-100 hover:bg-gray-200"}`}
          style={tab === "logs" ? { background: "var(--color-primary)" } : {}}
          onClick={() => setTab("logs")}
        >
          Waste Logs
        </button>
      </div>

      {tab === "dispose" ? (
        productsError ? (
          <EmptyState
            icon={WifiOff}
            title="Failed to Load Products"
            message="Check your connection and try again."
            onRetry={loadProducts}
            retryLabel="Reload"
          />
        ) : productsLoading ? (
          <div className="text-center py-8 text-gray-500">Loading bakery products...</div>
        ) : !products.length ? (
          <EmptyState
            icon={Trash2}
            title="No Bakery Products"
            message="No bakery products are available for disposal in this branch."
          />
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <form onSubmit={handleSubmitDisposal} className="space-y-4 max-w-xl">
              <div>
                <label className="text-sm font-medium text-gray-700">Bakery Product</label>
                <div className="mt-1">
                  <Select
                    value={productOptions.find((option) => option.value === String(productId)) || null}
                    onChange={(selected) => setProductId(selected?.value || "")}
                    options={productOptions}
                    placeholder="Search bakery product..."
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base) => ({
                        ...base,
                        borderRadius: "0.5rem",
                        borderColor: "#e5e7eb",
                        minHeight: "40px",
                      }),
                      valueContainer: (base) => ({ ...base, padding: "0 12px" }),
                    }}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Waste Quantity</label>
                <input
                  type="number"
                  min="1"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Reason (Optional)</label>
                <textarea
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none"
                  placeholder="e.g. Damaged during handling"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              <div className="pt-2">
                <PrimaryButton type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Record Disposal"}
                </PrimaryButton>
              </div>
            </form>
          </div>
        )
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Filter</h3>
            <div className="flex items-center gap-3 bg-[#f6f6f8] p-4 rounded-xl flex-wrap">
              <label className="text-sm font-semibold text-gray-700">Select Date Range:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
              {(startDate && endDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setAppliedStartDate(startDate);
                    setAppliedEndDate(endDate);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Submit
                </button>
              )}
              {(appliedStartDate || appliedEndDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setAppliedStartDate("");
                    setAppliedEndDate("");
                    setCurrentPage(1);
                  }}
                  className="text-sm font-semibold text-primary hover:text-primary-dark"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="mb-4">
            <SearchInput
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search waste logs by product..."
            />
          </div>

          {error ? (
            <EmptyState
              icon={WifiOff}
              title="Failed to Load Waste Logs"
              message="Check your connection and try again."
              onRetry={loadLogs}
              retryLabel="Reload"
            />
          ) : loading ? (
            <div className="text-center py-8 text-gray-500">Loading waste logs...</div>
          ) : !rows.length ? (
            <EmptyState
              icon={Trash2}
              title="No Waste Logs"
              message="No disposal records found for your current filter."
            />
          ) : (
            <>
              <div className={tableContainer}>
                <table className={tableBase}>
                  <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
                    <tr>
                      <th className={tableHeader + " text-center"}>Date</th>
                      <th className={tableHeader + " text-center"}>Time</th>
                      <th className={tableHeader + " text-center"}>Product</th>
                      <th className={tableHeader + " text-center"}>Qty Wasted</th>
                      <th className={tableHeader + " text-center"}>Unit</th>
                      <th className={tableHeader + " text-center"}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className={tableRow + " border-b last:border-0"}>
                        <td className={tableCell + " text-center"}>{formatDate(row.created_at || row.createdAt)}</td>
                        <td className={tableCell + " text-center"}>{formatTime(row.created_at || row.createdAt)}</td>
                        <td className={tableCell + " text-center"}>{row.product?.name || "-"}</td>
                        <td className={tableCell + " text-center tabular-nums"}>{Number(row.qty) || 0}</td>
                        <td className={tableCell + " text-center"}>{row.product?.unit_type || "pcs"}</td>
                        <td className={tableCell + " text-center"}>{row.reason || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col items-center gap-2 mt-3">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
                <p className="text-sm" style={{ color: "var(--color-text)", opacity: 0.7 }}>
                  Showing {totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} -{" "}
                  {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} items | Page {currentPage} of {totalPages}
                </p>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DisposalPage;
